/*
 * A marketplace offer, as a BUYER reads it.
 *
 * Two gateway documents describe one offer and the frontend needs both:
 *
 *   GET /api/v1/marketplace/offers/:slug   — the public projection. Carries
 *     slug, offer_type, title, summary, kind, pricing, sizes, publisher,
 *     version, maintainer, trial, access, attribution, requires and
 *     checkout_available. It does NOT carry description, execution, or the
 *     schemas.
 *   GET /api/v1/marketplace/offers/:slug/manifest — the published manifest,
 *     byte-for-byte. It DOES carry description and execution.
 *
 * (Verified against bluebear-gateway origin/dev: `publicOfferDetail` in
 * src/services/marketplace/marketplace.service.ts, and the manifest the seed
 * writes in scripts/seed-marketplace-readiness-audit.ts.)
 *
 * So the detail is the source of truth for everything it projects and the
 * manifest fills in only what the detail omits. Nothing here invents a field:
 * an offer whose manifest is unreachable renders without a description rather
 * than with a guessed one, and an offer with no price shows no price.
 *
 * `execution.review_assignee_user_id` is deliberately not read. It is in the
 * public manifest, but naming the reviewer who will be assigned is not the
 * storefront's business.
 */
import { formatUsd } from './marketplaceReview.js';

/* One money formatter across the marketplace surfaces. The review page owns it
 * because it landed first; re-exported here so a consumer of the storefront
 * does not have to know that. */
export { formatUsd };

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
const asArray = (value) => (Array.isArray(value) ? value : []);
const asText = (value) => (typeof value === 'string' ? value.trim() : '');

export function safePublicLaunchUrl(value) {
  const text = asText(value);
  if (!text) return '';
  try {
    const url = new URL(text);
    return url.protocol === 'https:' ? url.toString() : '';
  } catch {
    return '';
  }
}

function moneyAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

/** One row of the size comparison. `includes` and `description` render only if carried. */
function normalizeSize(entry) {
  const size = asObject(entry);
  const code = asText(size.code);
  if (!code) return null;
  return {
    code,
    label: sizeLabel(code),
    amountUsd: moneyAmount(size.amount_usd),
    currency: asText(size.currency) || 'USD',
    description: asText(size.description) || asText(size.summary),
    includes: asArray(size.includes).map(asText).filter(Boolean),
  };
}

/** `standard` → `Standard`. A code the publisher wrote in words is left alone. */
export function sizeLabel(code) {
  const text = asText(code);
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).replace(/[_-]+/g, ' ');
}

/**
 * The offer the page renders.
 *
 * `detail` is required; `manifest` is optional and only ever fills gaps.
 */
export function normalizeOffer(detail, manifest = null) {
  const row = asObject(detail);
  if (!asText(row.slug)) return null;
  const published = asObject(manifest);

  const maintainer = asObject(row.maintainer);
  const trial = asObject(row.trial);
  const access = asObject(row.access);
  const attribution = asObject(row.attribution);
  const execution = asObject(published.execution);

  return {
    slug: asText(row.slug),
    offerType: asText(row.offer_type),
    title: asText(row.title) || asText(published.title),
    summary: asText(row.summary) || asText(published.summary),
    kind: asText(row.kind) || asText(published.kind),
    publisher: asText(row.publisher),
    version: asText(row.version),
    /* Only the manifest carries prose. No fallback text: a missing description
     * is a blank section, not an invented one. */
    description: asText(published.description),
    pricing: {
      model: asText(row.pricing?.model),
      amountUsd: moneyAmount(row.pricing?.amount_usd),
      currency: asText(row.pricing?.currency) || 'USD',
    },
    sizes: asArray(row.sizes).map(normalizeSize).filter(Boolean),
    maintainer: {
      name: asText(maintainer.name),
      updateCadence: asText(maintainer.update_cadence),
      lastUpdated: asText(maintainer.last_updated),
    },
    trial: {
      available: trial.available === true,
      reason: asText(trial.reason),
    },
    access: {
      model: asText(access.model),
      contact: asText(access.contact),
      launchUrl: safePublicLaunchUrl(access.launch_url),
      launchLabel: asText(access.launch_label) || 'Try this offer',
    },
    attribution: {
      emits: asArray(attribution.emits).map(asText).filter(Boolean),
      settlement: asText(attribution.settlement),
    },
    requires: asArray(row.requires).filter((entry) => entry && typeof entry === 'object'),
    execution: {
      runner: asText(execution.runner),
      tool: asText(execution.tool),
      autoDispatch: execution.auto_dispatch === true,
      review: asText(execution.review),
    },
    inputSchema: asObject(published.input_schema),
    checkoutAvailable: row.checkout_available === true,
    /* The public route only ever serves status='listed' offers and does not
     * project the column, so this is null in practice. It is read anyway so a
     * fixture or a future draft-preview surface can be labelled honestly — and
     * so a real listing is never labelled. */
    status: asText(row.status) || null,
  };
}

/**
 * True only when the offer says, in its own data, that it is not a live listing.
 * An offer with no status is a real listing: the public route serves nothing else.
 */
export function isIllustrativeOffer(offer) {
  return Boolean(offer?.status) && offer.status !== 'listed';
}

// ── Requirements ────────────────────────────────────────────────────────────

const READ_ONLY_SCOPE = /(^|[:._-])read(only)?$/i;

function connectionLabel(name) {
  const text = asText(name);
  if (!text) return 'a connected system';
  if (text.toLowerCase() === 'github') return 'GitHub';
  if (text.toLowerCase() === 'gitlab') return 'GitLab';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A typed dependency in buyer words.
 *
 * The gateway's `requires[]` is machine-typed —
 * `{capability|category|connection|review, kind?, scopes?}` — and this is the
 * one place that turns a type into a sentence. Anything it does not recognise
 * falls back to the first string it carries rather than disappearing: a
 * requirement a buyer cannot see is a requirement they discover at checkout.
 */
export function requirementSentence(entry) {
  const item = asObject(entry);
  const scopes = asArray(item.scopes).map(asText).filter(Boolean);

  if (asText(item.connection)) {
    const label = connectionLabel(item.connection);
    if (scopes.length && scopes.every((scope) => READ_ONLY_SCOPE.test(scope))) {
      return `read-only ${label} access`;
    }
    if (scopes.length) return `${label} access (${scopes.join(', ')})`;
    return `${label} access`;
  }

  if (asText(item.review)) return 'a human attestation';

  const capability = asText(item.capability) || asText(item.category);
  if (capability) {
    const kind = asText(item.kind);
    if (kind === 'validator') return `a published validator (${capability})`;
    if (kind === 'rules') return `a published rule set (${capability})`;
    if (kind) return `a published ${kind} (${capability})`;
    return `the ${capability} capability`;
  }

  const firstString = Object.values(item).find((value) => asText(value));
  return asText(firstString);
}

/** Every requirement as a sentence, in the order the publisher declared them. */
export function requirementSentences(requires) {
  return asArray(requires).map(requirementSentence).filter(Boolean);
}

/** "Requires: read-only GitHub access; a human attestation" */
export function requirementSummary(requires) {
  const sentences = requirementSentences(requires);
  if (!sentences.length) return '';
  return `Requires: ${sentences.join('; ')}`;
}

function hasRequirement(offer, predicate) {
  return asArray(offer?.requires).some((entry) => predicate(asObject(entry)));
}

// ── How it works ────────────────────────────────────────────────────────────

/**
 * Three steps, derived from the offer rather than written for one product.
 *
 * Each step reads the fields that are actually present: the connection
 * requirement names what you grant, `execution.runner` plus a rules
 * requirement name what runs, and a review requirement is what makes step
 * three an attestation instead of a delivery. An offer with none of those
 * still gets three honest steps.
 */
export function howItWorksSteps(offer) {
  const connections = asArray(offer?.requires)
    .map(asObject)
    .filter((entry) => asText(entry.connection));
  const connectionText = connections.length
    ? connections.map(requirementSentence).join(' and ')
    : '';
  const rules = hasRequirement(offer, (entry) => asText(entry.kind) === 'rules');
  const review = hasRequirement(offer, (entry) => asText(entry.review));
  const runner = asText(offer?.execution?.runner);
  const noun = asText(offer?.kind) === 'solution' ? 'solution' : 'capability';

  return [
    {
      number: '01',
      title: connectionText ? 'Connect what it needs' : 'Give it what it needs',
      body: connectionText
        ? `Grant ${connectionText}. Nothing else is asked for, and the connection is the only access the run gets.`
        : 'Send the inputs the offer asks for. Nothing else is read.',
    },
    {
      number: '02',
      title: 'An agent runs it',
      body: [
        runner ? `The ${runner} agent runs the ${noun}` : `An agent runs the ${noun}`,
        rules ? ' against a published rule set, so two runs are judged the same way.' : '.',
      ].join(''),
    },
    review
      ? {
        number: '03',
        title: 'A named person attests, then it is released',
        body: 'A human reviewer reads the result, corrects what the agent got wrong, and puts their name on it. You receive it after that, not before.',
      }
      : {
        number: '03',
        title: 'You receive the result',
        body: 'The result is returned with the record of what ran, which version, and what it was judged against.',
      },
  ];
}

// ── Price ───────────────────────────────────────────────────────────────────

/**
 * What this costs, from what the gateway said and nothing else.
 *
 * Sizes first, `pricing.amount_usd` when there are none, and null when neither
 * carries an explicit amount. Zero is a real free price; missing pricing is not.
 */
export function offerPriceRange(offer) {
  const amounts = asArray(offer?.sizes)
    .map((size) => size.amountUsd)
    .filter((amount) => Number.isFinite(amount) && amount >= 0)
    .sort((a, b) => a - b);

  if (amounts.length) {
    return { low: amounts[0], high: amounts[amounts.length - 1], currency: offer.sizes[0]?.currency || 'USD' };
  }
  const single = moneyAmount(offer?.pricing?.amountUsd);
  if (single === null) return null;
  return { low: single, high: single, currency: offer?.pricing?.currency || 'USD' };
}

/** "$3,500.00" for one price, "Free" for zero/free, and '' for none. */
export function offerPriceLabel(offer) {
  if (asText(offer?.pricing?.model).toLowerCase() === 'free') return 'Free';
  const range = offerPriceRange(offer);
  if (!range) return '';
  if (range.low === 0 && range.high === 0) return 'Free';
  if (range.low === range.high) return formatUsd(range.low);
  return `${formatUsd(range.low)} – ${formatUsd(range.high)}`;
}

const PRICING_MODEL_WORDS = {
  per_execution: 'per completed run',
  per_call: 'per call',
  per_seat: 'per seat',
  subscription: 'per billing period',
};

/** The pricing model in words, or '' when the offer did not name one. */
export function pricingModelLabel(offer) {
  const model = asText(offer?.pricing?.model);
  if (!model) return '';
  if (model === 'free') return '';
  return PRICING_MODEL_WORDS[model] || model.replace(/[_-]+/g, ' ');
}

/** What the offer says about trying it first. Never softened, never invented. */
export function trialSentence(offer) {
  const trial = asObject(offer?.trial);
  if (trial.available === true) {
    return trial.reason ? `A trial is available. ${trial.reason}` : 'A trial is available.';
  }
  if (trial.reason) return `No trial. ${trial.reason}`;
  return 'No trial.';
}

// ── Errors ──────────────────────────────────────────────────────────────────

const ERROR_NOTICES = {
  OFFER_NOT_FOUND: 'That offer is not listed.',
  INVALID_CONTACT_EMAIL: 'That email address does not look right. Check it and try again.',
  MARKETPLACE_RATE_LIMITED:
    'Too many requests from this network in the last minute. Wait a minute and send it again.',
};

/**
 * A gateway error in words a buyer can act on.
 *
 * The codes are the frozen ones from the public surface; anything else falls
 * back to what the gateway said, and then to the caller's sentence. No status
 * codes reach the screen.
 */
export function offerErrorNotice(err, fallback = 'Something went wrong.') {
  const status = err?.response?.status ?? null;
  const code = err?.response?.data?.code ?? null;
  if (status === 429) {
    return { code: code || 'MARKETPLACE_RATE_LIMITED', status, message: ERROR_NOTICES.MARKETPLACE_RATE_LIMITED };
  }
  if (code && ERROR_NOTICES[code]) {
    return { code, status, message: ERROR_NOTICES[code] };
  }
  const message = err?.response?.data?.error || err?.message || fallback;
  return { code, status, message: message || fallback };
}

/** True when the gateway said this slug is not a listed offer. */
export function isOfferNotFound(err) {
  return err?.response?.status === 404 || err?.response?.data?.code === 'OFFER_NOT_FOUND';
}

/** The one client-side email check. The gateway is still the authority. */
export function isLikelyEmail(value) {
  const text = asText(value);
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(text) && text.length <= 320;
}
